import { Injectable, Injector } from '@angular/core';
import { AbstractControl, AsyncValidator, ValidationErrors, Validators, AsyncValidatorFn } from '@angular/forms';
import { createSelector, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { take, switchMap, catchError, finalize, map, filter, withLatestFrom } from 'rxjs/operators';
import {
  getExperiencesState,
  getPurchaseOrderState,
  getPurchaseOrderValidMasks,
  getPurchaseOrderFullState,
  OrderBuilderState,
} from '../../../../reducers';
import {
  PurchaseOrderValidateStartAction,
  PurchaseOrderValidateEndAction,
  SetPurchaseOrderAsyncErrorsAction,
  ValidationEndedAction,
} from '../../actions/purchase-order-actions/purchase-order.actions';
import { PurchaseOrderAsyncErrors } from '@ordersnproducts/modules/order-builder/modules/location/models/purchase-order-async-errors';
import { some } from 'lodash';
import { matchPurchaseOrderWithPattern } from '@ordersnproducts/modules/shared/validation/purchase-order.validation';
import { getAppInjector } from '@ordersnproducts/utils/app-injector';

const getPurchaseOrderValidationData = createSelector(
  getPurchaseOrderState,
  getExperiencesState,
  (purchaseOrderState, experiencesState) => {
    return {
      isValidationRequired: purchaseOrderState.required,
      isOnline: experiencesState.isOnline === 1,
    };
  },
);

class PurchaseOrderData {
  private store: Store<OrderBuilderState>;

  constructor() {
    const auxInjector: Injector = getAppInjector();
    this.store = auxInjector.get<any>(Store);
  }

  getState$() {
    return this.store.select(getPurchaseOrderFullState);
  }

  getExperience$() {
    return this.store.select(getExperiencesState);
  }
}

/**
 * PO validator that uses masks. UK RMX only.
 */
@Injectable()
export class PurchaseOrderFormatValidator implements AsyncValidator {

  constructor(private store: Store<OrderBuilderState>) {}

  static maskValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<any> => {
      const poData = new PurchaseOrderData();
      const state$ = poData.getState$().pipe(
        filter(data => data.fetching === false),
        take(1),
        withLatestFrom(poData.getExperience$()),
        map(([data, experience]) => {
          if (experience.isOnline === 0) {
            return null;
          }

          if (data.required == null || data.required === false) {
            return null;
          } else {
            control.setValidators(Validators.required);
          }

          if (data.required === true && data.validMasks && data.validMasks.length === 0) {
            if (control.value !== undefined && control.value.length > 0) {
              return null;
            }
            return { invalidPurchaseOrderRequired: { hasError: true } };
          }

          if (data.required === true && data.validMasks && data.validMasks.length > 0 ) {
            const matchArray = data.validMasks.map((mask) => some(
              mask, pattern => matchPurchaseOrderWithPattern(mask, control.value),
            ));
            const correctPattern = matchArray.some((value) => value);
            if (correctPattern) {
              return null;
            }
            return { invalidPurchaseOrderPattern: { hasError: true } };
          }
          return null;
        }),
      );
      return state$;
    };
  }

  /**
   * Calls the API to validate the content of the purchase-order field.
   * @param {AbstractControl} control
   * @return {Promise<ValidationErrors | null> | Observable<ValidationErrors | null>}
   */
  validate(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    this.store.dispatch(new PurchaseOrderValidateStartAction());

    return this.store.select(getPurchaseOrderValidationData).pipe(
      switchMap(({ isOnline, isValidationRequired }) => {
        if (!isOnline) {
          return of(null);
        }

        // validation not required
        if (isValidationRequired == null || !isValidationRequired) {
          return of(null);
        } else {
          control.setValidators(Validators.required);
        }

        // string is empty
        if (isValidationRequired && (control.value == null || control.value === '')) {
          const error: PurchaseOrderAsyncErrors = { required: true };
          return of(error);
        }

        // actual validation
        return this.store.select(getPurchaseOrderValidMasks).pipe(
          map(patterns => some(patterns, pattern => matchPurchaseOrderWithPattern(pattern, control.value))),
          map(isValid => {
            const error: PurchaseOrderAsyncErrors = !isValid
              ? {
                  invalidPurchaseOrder: { hasError: true },
                }
              : null;
            return error;
          }),
          catchError(err => {
            return err;
          }),
        );
      }),
      take(1),
      catchError(oops => {
        console.warn(oops);
        const error: PurchaseOrderAsyncErrors = {
          invalidPurchaseOrder: { hasError: true, message: 'Purchase Order Error' },
        };
        this.store.dispatch(new ValidationEndedAction());
        this.store.dispatch(new SetPurchaseOrderAsyncErrorsAction(error));
        return of(error);
      }),
      finalize(() => {
        this.store.dispatch(new PurchaseOrderValidateEndAction());
      }),
    );
  }
}
